<script setup lang="ts">
	import { Building2, Info, KeyRound, TriangleAlert, UserRound } from 'lucide-vue-next'
	import { computed, ref, watch } from 'vue'
	import { Collapse } from 'vue-collapsed'
	import { useRouter } from 'vue-router'

	import HasPermission from '@/components/HasPermission.vue'
	import StyledDateInput from '@/components/StyledDateInput.vue'
	import FormField from '@/components/form/FormField.vue'
	import OptionCard from '@/components/form/OptionCard.vue'
	import SearchSelect from '@/components/form/SearchSelect.vue'
	import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
	import { Button } from '@/components/ui/button'
	import {
		Card,
		CardContent,
		CardDescription,
		CardHeader,
		CardTitle,
	} from '@/components/ui/card'
	import { Checkbox } from '@/components/ui/checkbox'
	import { Input } from '@/components/ui/input'
	import { Label } from '@/components/ui/label'
	import {
		Select,
		SelectContent,
		SelectItem,
		SelectTrigger,
		SelectValue,
	} from '@/components/ui/select'

	import useAuthUser from '@/composables/useAuthUser'

	import { GenderTypes } from '@/enums/genderTypes'

	import { CompanyGroup } from '@/interfaces/companyGroup'
	import { CostCenter } from '@/interfaces/costCenter'
	import { Department } from '@/interfaces/department'

	import useInternalRoles from '@/internal/modules/roles/composables/useInternalRoles'
	import useInternalSearchUser from '@/internal/modules/users/composables/useInternalSearchUser'

	import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
	import useCompany from '@/modules/companies/composables/useCompany'
	import useCompanyCostCenters from '@/modules/companies/composables/useCompanyCostCenters'
	import useCompanyDepartment from '@/modules/companies/composables/useCompanyDepartment'
	import useCompanyUser from '@/modules/companies/composables/useCompanyUser'
	import { Company } from '@/modules/companies/interfaces/company'
	import { useCompanyGroups } from '@/modules/groups/composables/useCompanyGroups'
	import { Role } from '@/modules/roles/composables/useRoles'
	import useRoles from '@/modules/roles/composables/useRoles'
	import { actorRank, roleRank } from '@/modules/users/helpers/roleHierarchy'
	import { CreateUserForm } from '@/modules/users/interfaces/createUserForm'
	import { User } from '@/modules/users/interfaces/user'

	import useUser from '../composables/useUser'
	import useUsersSearch from '../composables/useUsersSearch'

	interface Props {
		companySelected?: number
		groupSelected?: number
		notHaveDefaultValueRole?: boolean
		corporationUser?: number
		defaultSupervisor?: User
	}

	const props = withDefaults(defineProps<Props>(), {
		notHaveDefaultValueRole: false,
	})

	const router = useRouter()

	interface Emits {
		(event: 'cancelForm'): void
		(event: 'sendForm'): void
	}

	const emits = defineEmits<Emits>()

	const { user } = useAuthUser()
	const supervisor = ref<User | undefined>(props.defaultSupervisor)

	const { company: companySelectedUser } = useCompany(
		{
			companyId: props.companySelected || user.value?.companyId,
		},
		{
			immediate: !!props.companySelected || !user.value?.internal,
		}
	)

	const { createForm, setCreateFormValue } = useUser({
		immediate: false,
	})

	if (props.defaultSupervisor) {
		createForm.value.supervisorId = props.defaultSupervisor.id
	}

	const { createUserMutationLoading } = useCompanyUser()

	const userFilters = ref<{
		company?: number
		supervisors?: string[]
		corporation?: number
	}>({
		company: undefined,
		corporation: undefined,
		supervisors: undefined,
	})

	const {
		setQuery: setUserQuery,
		isFetching: fetchingUsers,
		refetch: refetchUsers,
		data: users,
	} = useUsersSearch({
		automaticSearch: true,
		immediate: Boolean(props.companySelected),
		params: {
			filters: userFilters,
		},
		resetCache: true,
	})

	const {
		data: usersInternal,
		isLoading: internalLoading,
		query,
		refetch: refetchInternalUsers,
	} = useInternalSearchUser({
		immediate: false,
		params: {
			internalOnly: 1,
		},
	})

	const {
		companies,
		setQuery: setCompanyQuery,
		isFetching: fetchingCompanies,
	} = useCompaniesSearch({
		immediate: true,
	})

	const {
		data: roles,
		isLoading: fetchingRoles,
		setCompanyId,
		refetch: refetchRoles,
	} = useRoles({ immediate: false })

	// Jerarquía estricta y sin brincos (el backend valida igual): un Traveler solo
	// reporta a un Supervisor, un Supervisor solo a un Travel Manager, un TM solo al
	// Master. Copia de RoleHierarchy::SUPERVISOR_ROLES_FOR (BookingV2), que a su vez
	// tiene que decir lo mismo que el panel interno: si esta tabla ofrece un jefe que
	// el backend rechaza, el usuario se come un 422 al guardar.
	const ALLOWED_SUPERVISOR_ROLES: Record<string, string[]> = {
		Traveler: ['Supervisor'],
		Supervisor: ['Travel Manager'],
		'Travel Manager': ['Master'],
		Master: [],
	}

	const selectedRoleName = computed(
		() =>
			roles.value?.find((role) => role.id === Number(createForm.value.roleId))
				?.name ?? 'Traveler'
	)

	const allowedSupervisorRoles = computed(
		() => ALLOWED_SUPERVISOR_ROLES[selectedRoleName.value] ?? ['Supervisor']
	)

	const supervisorOptions = computed(() =>
		(users.value ?? []).filter((candidate) =>
			(candidate.roles ?? []).some((role) =>
				allowedSupervisorRoles.value.includes(role)
			)
		)
	)

	// Si el encargado elegido (o el default del flujo de reserva) ya no aplica
	// para el rol seleccionado, se limpia — el backend lo rechazaría igual.
	watch(
		allowedSupervisorRoles,
		(allowed) => {
			if (
				supervisor.value &&
				!(supervisor.value.roles ?? []).some((role) => allowed.includes(role))
			) {
				supervisor.value = undefined
				createForm.value.supervisorId = undefined
			}
		},
		{ immediate: true }
	)

	const filteredRoles = computed(() => {
		// Rango derivado de ASSIGNABLE_ROLES (helper), no una tabla numérica local:
		// así el test espejo de BookingV2 también vigila este filtro.
		const actorLevel = actorRank(user.value)
		// Sin rol cliente (p. ej. staff interno) esta lista no aplica: el alta
		// interna corre por rolesInternal, más abajo.
		if (actorLevel === null) return []
		return (roles.value ?? []).filter((role) => {
			const level = roleRank(role.name)
			if (level === -1) return false
			// El actor asigna de su nivel para abajo: Master todo, TM de TM para
			// abajo, Supervisor de Supervisor para abajo.
			return level >= actorLevel
		})
	})

	const { roles: rolesInternal, refetch: refetchInternalRoles } =
		useInternalRoles({
			immediate: false,
		})

	const company = ref<Company>()
	const checkedInternal = ref(false)

	const {
		data: groups,
		isFetching: fetchingGroups,
		setCompany,
		refetch: refetchGroups,
	} = useCompanyGroups({
		immediate: false,
	})

	const {
		departments,
		isFetching: fetchingDepartments,
		refetch: refetchDepartments,
		setCompanyId: setCompanyDep,
	} = useCompanyDepartment({
		immediate: false,
		params: {},
	})

	// Tipar los nombres evita el `as keyof CreateUserForm` en la plantilla: con
	// la unión completa, TypeScript exige la intersección de TODOS los valores
	// del formulario (o sea `never`) y obliga a un cast que apaga el chequeo.
	type TextFieldName = 'name' | 'secondName' | 'lastname' | 'secondLastname'

	interface TextField {
		label: string
		name: TextFieldName
		type: string
		placeholder: string
	}

	const fieldStructure: TextField[] = [
		{
			label: 'Nombre',
			name: 'name',
			type: 'text',
			placeholder: 'ej. John',
		},
		{
			label: 'Segundo nombre',
			name: 'secondName',
			type: 'text',
			placeholder: 'ej. Smith',
		},
		{
			label: 'Apellido',
			name: 'lastname',
			type: 'text',
			placeholder: '',
		},
		{
			label: 'Segundo apellido',
			name: 'secondLastname',
			type: 'text',
			placeholder: '',
		},
	]

	/**
	 * Notas al pie, SOLO en los campos que las necesitan.
	 *
	 * Nombre, apellido, fecha y sexo se explican solos: ponerles una nota
	 * debajo alarga la pantalla y entrena a la gente a ignorar el texto gris,
	 * justo donde después sí hace falta que lo lean.
	 *
	 * El correo sí la necesita: el campo se lee como un dato de contacto más,
	 * pero es la única dirección a la que sale la confirmación de una reserva
	 * hecha a su nombre. Conviene decirlo sin dramatizar: el alta funciona
	 * igual sin correo, lo que cambia es quién le avisa al viajero.
	 *
	 * Las que quedan cubren conceptos internos del sistema —grupo,
	 * departamento, centro de costo, encargado, rol— que no significan nada
	 * para quien da de alta a alguien por primera vez. Van juntas acá para que
	 * se lean como un texto y no se contradigan entre sí.
	 */
	const HINTS = {
		email:
			'A dónde le llegan sus confirmaciones de reserva, junto con los cambios y las cancelaciones. Si se deja vacío, el usuario se crea igual, pero de avisarle se encarga quien haga la reserva.',
		internal:
			'Personal de Enlace, no de una empresa cliente. Cambia los roles disponibles.',
		company: 'Empresa a la que pertenece. Define qué grupos, departamentos y centros de costo puede usar.',
		role: 'Qué puede hacer dentro del sistema. Viajero reserva para sí mismo; Supervisor aprueba a su equipo; Travel Manager administra la empresa; Master tiene control total.',
		group:
			'Define las políticas de viaje que se le aplican al reservar: cada reserva se revisa contra ellas y, según la regla, avisa o la bloquea. No cambia sus permisos en el sistema.',
		department:
			'Área a la que pertenece dentro de la empresa. Determina qué centros de costo puede usar.',
		costCenter:
			'Contra qué presupuesto se cargan sus viajes. Solo aparecen los del departamento elegido.',
		internalRole:
			'Rol dentro del equipo de Enlace, no de una empresa cliente. Define qué puede ver y hacer en el panel interno.',
		supervisor:
			'Quién aprueba sus solicitudes de viaje. La jerarquía la fija el rol: un Viajero reporta a un Supervisor, un Supervisor a un Travel Manager, y un Travel Manager al Master.',
	} as const

	const personalInformationFields = ['name', 'lastname', 'gender']

	const personalInformationFilled = computed(() => {
		return personalInformationFields.every(
			(field) => !!createForm.value[field as keyof CreateUserForm]
		)
	})

	const showAccessInfo = computed(() => !createForm.value.haveAccess)

	/**
	 * El correo solo es obligatorio para quien va a iniciar sesión —y para el
	 * alta interna, donde `companyId` queda en 0—. Es la misma condición que
	 * usa `rulesUserValidation` en useUser: se repite acá para que la etiqueta
	 * no diga una cosa y la validación otra.
	 */
	const emailRequired = computed(
		() => createForm.value.haveAccess || createForm.value.companyId === 0
	)

	const refetchAll = async () => {
		refetchUsers()
		refetchRoles()
		refetchGroups()
		refetchDepartments()
	}

	const companyUsed = async (companyId: number) => {
		userFilters.value.company = companyId

		setCreateFormValue('companyId', companyId)
		setCompanyId(companyId)
		setCompany(companyId.toString())
		setCompanyDep(companyId)

		refetchAll()
	}

	const refetchInternal = async () => {
		createForm.value.companyId = undefined
		company.value = undefined

		refetchInternalRoles()
		refetchInternalUsers()
	}

	// Mayores de 18: el backend valida `before:-18 years` y rechazaría el alta.
	const adjustDateForAgeLimit = computed(() => {
		const date = new Date()
		date.setFullYear(date.getFullYear() - 18)
		return date
	})


	watch(
		companySelectedUser,
		async (value) => {
			if (!value) return

			company.value = value
			await companyUsed(value.id)
		},
		{
			immediate: true,
		}
	)

	watch(company, async (value) => {
		if (!value) return

		await companyUsed(value.id)
	})

	// ── Centro de costo ──────────────────────────────────────────────────
	// Cuelga del departamento: el backend valida que el centro pertenezca al
	// `department_id` enviado, así que la UI no debe ofrecer otros.
	const { costCentersOfDepartment, isFetching: fetchingCostCenters } =
		useCompanyCostCenters(computed(() => company.value?.id))

	const availableCostCenters = computed(() =>
		costCentersOfDepartment(createForm.value.departmentId)
	)

	// Cambiar de departamento invalida el centro elegido: dejarlo puesto manda
	// una combinación que el backend rechaza.
	//
	// `flush: 'post'` NO es cosmético. Sin él la limpieza corría dentro del
	// mismo tick en que reka-ui cierra el Select de departamento, y el
	// re-render que provoca (cambia el placeholder y el `disabled` del Select
	// de centro de costo) se comía la restauración de estilos del
	// dismissable-layer: el <body> se quedaba con `pointer-events: none` y la
	// página entera dejaba de responder a clics. Aplazarlo al post-render deja
	// que reka-ui termine de cerrar antes de tocar nada.
	watch(
		() => createForm.value.departmentId,
		() => {
			createForm.value.costCenterId = undefined
		},
		{ flush: 'post' }
	)

	// ── Encargado ────────────────────────────────────────────────────────
	// La empresa puede no tener a nadie con el rol que exige la jerarquía. Sin
	// avisarlo, el combobox se ve igual que uno que aún está cargando y el alta
	// se traba sin explicación.
	const noSupervisorsAvailable = computed(
		() =>
			!!company.value &&
			!!createForm.value.roleId &&
			allowedSupervisorRoles.value.length > 0 &&
			!fetchingUsers.value &&
			supervisorOptions.value.length === 0
	)

	const requiredSupervisorRoles = computed(() =>
		allowedSupervisorRoles.value.join(' o ')
	)

	const hasCompanies = computed(() => {
		return companies.value?.length >= 2
	})

	// Un actor corporativo administra VARIAS empresas y a este punto la
	// empresa ya trae un default (la suya) con solo cargar el formulario: si
	// se bloqueara desde ahí, nunca podría elegir otra. Solo se cierra la
	// empresa una vez que rol, políticas, departamento o CECO ya tienen algo
	// cargado de ELLA — cambiarla después sí manda esos ids de otra empresa.
	const organizationFieldsChosen = computed(() =>
		!!(
			createForm.value.roleId ||
			createForm.value.companyGroupId ||
			createForm.value.departmentId ||
			createForm.value.costCenterId
		)
	)

	// ── Puente con los Select de shadcn ──────────────────────────────────
	// reka-ui modela el valor del select como string; el formulario y el API
	// trabajan con ids numéricos. El puente vive acá y no en cada campo para
	// que la conversión sea una sola y no se cuele un id como texto al payload.
	const numericSelect = (key: 'roleId' | 'companyGroupId' | 'departmentId' | 'costCenterId') =>
		computed({
			get: () =>
				createForm.value[key] != null ?
					String(createForm.value[key])
				:	undefined,
			set: (value: string | undefined) => {
				createForm.value[key] = value ? Number(value) : undefined
			},
		})

	const roleIdModel = numericSelect('roleId')
	const groupIdModel = numericSelect('companyGroupId')
	const departmentIdModel = numericSelect('departmentId')
	const costCenterIdModel = numericSelect('costCenterId')

	// Elegir rol reajusta a quién se puede poner de encargado.
	const onRoleChange = (value: string | undefined) => {
		const role = roles.value?.find((r) => r.id === Number(value))

		if (role) {
			userFilters.value.supervisors = [role.name]
			refetchUsers()
		}
	}






	const supervisorInternal = computed({
		get: () =>
			(usersInternal.value ?? []).find(
				(candidate: User) => candidate.id === createForm.value.supervisorId
			),
		set: (value: User | undefined) => {
			setCreateFormValue('supervisorId', value?.id)
		},
	})

	// El alta interna no tiene empresa: sin ella no hay grupos, departamentos ni
	// jerarquía que mostrar.
	const showCompanyFields = computed(() => !user.value?.internal || !checkedInternal.value)
</script>

<template>
	<!-- El padding es del formulario, no de quien lo monta: `ModalComponent`
	     entrega el slot a ras del DialogPanel, así que en los cinco modales que
	     usan este componente el contenido quedaba pegado al borde y el botón
	     "Volver" salía cortado. En la página el gutter de CreatePage se suma a
	     este y el resultado respira mejor. -->
	<form
		id="create-user-form"
		class="flex flex-col gap-5 p-4 sm:p-6"
		autocomplete="off"
	>
		<!-- ── Datos de la persona ───────────────────────────────────────── -->
		<Card>
			<CardHeader class="flex-row items-start gap-3 space-y-0">
				<span
					class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EEF2FB] text-[#253C70]"
				>
					<UserRound class="h-[18px] w-[18px]" />
				</span>
				<div class="min-w-0">
					<CardTitle class="text-base">Datos de la persona</CardTitle>
					<CardDescription>
						Deben coincidir con su identificación oficial: con estos datos se
						emiten los boletos y las reservas.
					</CardDescription>
				</div>
			</CardHeader>

			<CardContent class="grid gap-4 sm:grid-cols-2">
				<FormField
					v-for="(input, index) in fieldStructure"
					:key="index"
					:label="input.label"
					:required="!['secondName', 'secondLastname'].includes(input.name)"
					v-slot="{ id, describedBy }"
				>
					<Input
						:id="id"
						:aria-describedby="describedBy"
						:model-value="createForm[input.name] ?? ''"
						:type="input.type"
						:placeholder="input.placeholder"
						@update:model-value="
							(value) => setCreateFormValue(input.name, String(value))
						"
					/>
				</FormField>

				<FormField
					label="Fecha de nacimiento"
					class="[&_input]:!border-[1px]"
				>
					<!-- StyledDateInput fija h-[44px] y border-2 por dentro; el resto de
					     los controles miden 36px con borde de 1px. El alto se corrige por
					     `input-class`, pero el grosor no: `!border` y `!border-2` son
					     ambos !important y gana el que Tailwind emita después. Por eso el
					     borde va como variante descendente en el FormField de arriba, que
					     gana por especificidad y no por orden. -->
					<StyledDateInput
						v-model="createForm.birthDate"
						:max-date="adjustDateForAgeLimit"
						:start-date="adjustDateForAgeLimit"
						input-class="!h-9 !border-input !shadow-sm"
					/>
				</FormField>

				<FormField
					label="Sexo"
					required
					v-slot="{ id, describedBy }"
				>
					<Select v-model="createForm.gender">
						<SelectTrigger
							:id="id"
							:aria-describedby="describedBy"
						>
							<SelectValue placeholder="Seleccione una opción" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem
								v-for="gender in Object.values(GenderTypes)"
								:key="gender"
								:value="gender"
							>
								{{ gender }}
							</SelectItem>
						</SelectContent>
					</Select>
				</FormField>

				<FormField
					:label="
						emailRequired ? 'Correo electrónico' : (
							'Correo electrónico (opcional)'
						)
					"
					:required="emailRequired"
					:hint="HINTS.email"
					v-slot="{ id, describedBy }"
				>
					<Input
						:id="id"
						v-model="createForm.email as string"
						:aria-describedby="describedBy"
						type="email"
						placeholder="ej. nombre@empresa.com"
						autocomplete="off"
					/>
				</FormField>
			</CardContent>
		</Card>

		<!-- ── Acceso al sistema ─────────────────────────────────────────── -->
		<Card>
			<CardHeader class="flex-row items-start gap-3 space-y-0">
				<span
					class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EEF2FB] text-[#253C70]"
				>
					<KeyRound class="h-[18px] w-[18px]" />
				</span>
				<div class="min-w-0">
					<CardTitle class="text-base">Acceso al sistema</CardTitle>
					<CardDescription>
						Si entra a Enlacebooking o si existe solo como viajero.
					</CardDescription>
				</div>
			</CardHeader>

			<CardContent class="space-y-4">
				<div
					v-if="!checkedInternal"
					role="radiogroup"
					aria-label="Acceso al sistema"
					class="grid gap-3 sm:grid-cols-2"
				>
					<OptionCard
						title="Con acceso"
						description="Recibe un correo para crear su contraseña y reserva por su cuenta."
						detail="Requiere correo"
						:selected="createForm.haveAccess"
						@select="createForm.haveAccess = true"
					/>
					<OptionCard
						title="Sin acceso"
						description="Existe solo como viajero: alguien más reserva a su nombre."
						detail="No inicia sesión"
						:selected="!createForm.haveAccess"
						@select="createForm.haveAccess = false"
					/>
				</div>

				<div v-if="!createForm.haveAccess && user?.internal">
					<HasPermission
						:name-permission="'create-internal'"
						:entity-type="'App\\Models\\User'"
					>
						<div
							v-if="!companySelected"
							class="rounded-lg border border-gray-200 p-3"
						>
							<div class="flex items-start gap-3">
								<Checkbox
									id="internal-user"
									:model-value="checkedInternal"
									class="mt-0.5"
									@update:model-value="
										(value) => {
											checkedInternal = value === true

											if (value === true) {
												refetchInternal()
											}
										}
									"
								/>
								<div class="min-w-0">
									<Label
										for="internal-user"
										class="text-sm font-medium text-gray-900"
									>
										Usuario interno
									</Label>
									<p class="mt-0.5 text-xs leading-snug text-gray-500">
										{{ HINTS.internal }}
									</p>
								</div>
							</div>
						</div>
					</HasPermission>
				</div>

				<Alert variant="info">
					<template #icon><Info /></template>
					<AlertDescription>
						{{
							showAccessInfo ?
								'Este usuario no podrá tener acceso al sistema.'
							:	'Se le enviará un correo con la invitación para que pueda acceder al sistema.'
						}}
					</AlertDescription>
				</Alert>

				<Alert
					v-if="!router.currentRoute.value.fullPath.includes('/users/create')"
					variant="info"
				>
					<template #icon><Info /></template>
					<AlertDescription>
						Te he asignado el rol de 'Viajero'. Si deseas cambiarlo, dirígete a
						la sección de usuario y créalo desde ahí.
					</AlertDescription>
				</Alert>
			</CardContent>
		</Card>

		<!-- ── Organización ──────────────────────────────────────────────── -->
		<Collapse
			:when="personalInformationFilled"
			class="v-collapse"
		>
			<Card>
				<CardHeader class="flex-row items-start gap-3 space-y-0">
					<span
						class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EEF2FB] text-[#253C70]"
					>
						<Building2 class="h-[18px] w-[18px]" />
					</span>
					<div class="min-w-0">
						<CardTitle class="text-base">Organización</CardTitle>
						<CardDescription>
							Dónde encaja dentro de la empresa: define sus permisos, quién le
							aprueba y contra qué presupuesto viaja.
						</CardDescription>
					</div>
				</CardHeader>

				<CardContent class="grid gap-4 sm:grid-cols-2">
					<!-- Empresa -->
					<FormField
						v-if="showCompanyFields"
						label="Empresa"
						required
						:hint="HINTS.company"
						v-slot="{ id, describedBy }"
					>
						<!-- Se bloquea solo cuando YA hay rol, políticas, departamento o CECO
						     elegidos para esta empresa: cambiarla sin resetear esos cuatro
						     campos mandaría un id de otra empresa al crear el usuario. Antes de
						     eso queda libre — un corporativo con varias empresas necesita poder
						     cambiar el default que trae el formulario al cargar. -->
						<SearchSelect
							:id="id"
							:described-by="describedBy"
							:model-value="company"
							:options="companies ?? []"
							:loading="fetchingCompanies"
							:disabled="!!company && organizationFieldsChosen"
							:clearable="hasCompanies"
							:display-value="(option: Company) => option.name"
							:option-key="(option: Company) => option.id"
							:placeholder="
								props.companySelected ?
									(companySelectedUser?.name ?? 'Buscar empresa')
								:	'Buscar empresa'
							"
							search-placeholder="Buscar empresa…"
							empty-text="Ninguna empresa coincide"
							@update:model-value="
								(value) => {
									company = value

									if (!value) {
										setCreateFormValue('companyId', undefined)
										companySelectedUser = undefined
									}
								}
							"
							@search="(value: string) => setCompanyQuery(value)"
						/>
					</FormField>

					<template v-if="company">
						<!-- Rol -->
						<HasPermission
							:entity-type="'App\\Models\\Company'"
							name-permission="view-roles"
						>
							<FormField
								label="Rol"
								required
								:hint="HINTS.role"
								:class="
									router.currentRoute.value.fullPath.includes(
										'/services/hotel/availability'
									) ?
										'hidden'
									:	''
								"
								v-slot="{ id, describedBy }"
							>
								<Select
									v-model="roleIdModel"
									:disabled="fetchingRoles || filteredRoles.length === 0"
									@update:model-value="
										(value) => onRoleChange(value as string | undefined)
									"
								>
									<SelectTrigger
										:id="id"
										:aria-describedby="describedBy"
									>
										<SelectValue
											:placeholder="
												fetchingRoles ? 'Cargando roles…'
												: filteredRoles.length === 0 ?
													'Esta empresa no tiene roles disponibles'
												:	'Seleccione un rol'
											"
											/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem
											v-for="role in filteredRoles"
											:key="role.id"
											:value="String(role.id)"
										>
											{{ role.name }}
										</SelectItem>
									</SelectContent>
								</Select>
							</FormField>
						</HasPermission>

						<!-- Políticas de viaje -->
						<HasPermission
							name-permission="view-groups"
							:entity-type="'App\\Models\\Company'"
						>
							<FormField
								label="Políticas de viaje"
								:hint="HINTS.group"
								v-slot="{ id, describedBy }"
							>
								<!-- Mientras carga NO se dice "no tiene": era un falso
								     negativo que salía siempre al elegir empresa. -->
								<Select
									v-model="groupIdModel"
									:disabled="!!groupSelected || fetchingGroups || !groups?.length"
								>
									<SelectTrigger
										:id="id"
										:aria-describedby="describedBy"
									>
										<SelectValue
											:placeholder="
												fetchingGroups ? 'Cargando grupos…'
												: groups?.length ? 'Seleccione una política de viaje'
												: 'Esta empresa no tiene políticas de viaje'
											"
											/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem
											v-for="group in groups ?? []"
											:key="group.id"
											:value="String(group.id)"
										>
											{{ group.name }}
										</SelectItem>
									</SelectContent>
								</Select>
							</FormField>
						</HasPermission>

						<!-- Departamento y centro de costo van en HasPermission
						     separados: el componente renderiza un <div>, así que dos
						     campos dentro del mismo comparten celda del grid. -->
						<HasPermission
							name-permission="view-departments"
							:entity-type="'App\\Models\\Company'"
						>
							<FormField
								label="Departamento"
								:hint="HINTS.department"
								v-slot="{ id, describedBy }"
							>
								<Select
									v-model="departmentIdModel"
									:disabled="fetchingDepartments || departments.length === 0"
								>
									<SelectTrigger
										:id="id"
										:aria-describedby="describedBy"
									>
										<SelectValue
											:placeholder="
												fetchingDepartments ? 'Cargando departamentos…'
												: departments.length ? 'Seleccione un departamento'
												: 'Esta empresa no tiene departamentos'
											"
											/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem
											v-for="department in departments"
											:key="department.id"
											:value="String(department.id)"
										>
											{{ department.name }}
										</SelectItem>
									</SelectContent>
								</Select>
							</FormField>
						</HasPermission>

						<HasPermission
							name-permission="view-departments"
							:entity-type="'App\\Models\\Company'"
						>
							<FormField
								label="Centro de costo"
								:hint="HINTS.costCenter"
								v-slot="{ id, describedBy }"
							>
								<Select
									v-model="costCenterIdModel"
									:disabled="
										!createForm.departmentId ||
										fetchingCostCenters ||
										availableCostCenters.length === 0
									"
								>
									<SelectTrigger
										:id="id"
										:aria-describedby="describedBy"
									>
										<SelectValue
											:placeholder="
												!createForm.departmentId ?
													'Elija primero un departamento'
												: fetchingCostCenters ? 'Cargando…'
												: availableCostCenters.length === 0 ?
													'Este departamento no tiene centros de costo'
												:	'Seleccione un centro de costo'
											"
											/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem
											v-for="costCenter in availableCostCenters"
											:key="costCenter.id"
											:value="String(costCenter.id)"
										>
											{{ costCenter.key }} — {{ costCenter.name }}
										</SelectItem>
									</SelectContent>
								</Select>
							</FormField>
						</HasPermission>

						<!-- Supervisor -->
						<HasPermission
							name-permission="view-users"
							:entity-type="'App\\Models\\Company'"
						>
							<FormField
								label="Supervisor"
								:hint="HINTS.supervisor"
								v-slot="{ id, describedBy }"
							>
								<SearchSelect
									:id="id"
									:described-by="describedBy"
									:model-value="supervisor"
									:options="supervisorOptions"
									:loading="fetchingUsers"
									:display-value="
										(option: User) => `${option.name} ${option.lastname}`
									"
									:option-key="(option: User) => option.id"
									placeholder="Buscar supervisor"
									search-placeholder="Buscar por nombre o correo…"
									empty-text="Nadie coincide con esa búsqueda"
									@update:model-value="
										(value) => {
											supervisor = value
											createForm.supervisorId = value?.id
										}
									"
									@search="(value: string) => setUserQuery(value)"
								>
									<!-- Dos líneas truncadas, no nombre y correo lado a lado:
									     así la fila mide siempre lo mismo y la lista no crece
									     hasta obligar al popover a abrirse hacia arriba. -->
									<template #option="{ option }">
										<span class="flex min-w-0 flex-col">
											<span class="truncate text-xs font-medium">
												{{ option.name }} {{ option.lastname }}
											</span>
											<span class="truncate text-[11px] text-gray-500">
												{{ option.email ?? option.companyName ?? 'Sin empresa' }}
											</span>
										</span>
									</template>
								</SearchSelect>
							</FormField>
						</HasPermission>

						<!-- Sin candidatos: el selector vacío no distingue "no hay" de
						     "todavía carga". Va fuera del HasPermission anterior para que
						     sea hija directa del grid y pueda ocupar las dos columnas. -->
						<HasPermission
							v-if="noSupervisorsAvailable"
							name-permission="view-users"
							:entity-type="'App\\Models\\Company'"
							class="sm:col-span-2"
						>
							<Alert variant="warning">
								<template #icon><TriangleAlert /></template>
								<AlertTitle>
									Esta empresa todavía no tiene ningún
									{{ requiredSupervisorRoles }}.
								</AlertTitle>
								<AlertDescription>
									Para el rol elegido, el supervisor tiene que ser
									{{ requiredSupervisorRoles }}. Puede crear el usuario sin
									supervisor y asignárselo después, o dar de alta primero a
									quien vaya a aprobarle los viajes.
								</AlertDescription>
							</Alert>
						</HasPermission>
					</template>

					<!-- Alta interna: otros roles y otro universo de supervisores -->
					<template v-if="checkedInternal">
						<HasPermission
							:name-permission="'viewAny-internal'"
							:entity-type="'roles'"
						>
							<FormField
								label="Rol interno"
								required
								:hint="HINTS.internalRole"
								v-slot="{ id, describedBy }"
							>
								<Select v-model="roleIdModel">
									<SelectTrigger
										:id="id"
										:aria-describedby="describedBy"
									>
										<SelectValue placeholder="Seleccione un rol interno" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem
											v-for="role in rolesInternal ?? []"
											:key="role.id"
											:value="String(role.id)"
										>
											{{ role.name }}
										</SelectItem>
									</SelectContent>
								</Select>
							</FormField>
						</HasPermission>

						<HasPermission
							:name-permission="'viewAny-internal'"
							:entity-type="'App\\Models\\User'"
						>
							<FormField
								label="Supervisor"
								:hint="HINTS.supervisor"
								v-slot="{ id, describedBy }"
							>
								<SearchSelect
									:id="id"
									:described-by="describedBy"
									v-model="supervisorInternal"
									:options="usersInternal ?? []"
									:loading="internalLoading"
									:display-value="
										(option: User) => `${option.name} ${option.lastname}`
									"
									:option-key="(option: User) => option.id"
									placeholder="Buscar supervisor"
									search-placeholder="Buscar por nombre o correo…"
									empty-text="Nadie coincide con esa búsqueda"
									@search="(value: string) => (query = value)"
								>
									<template #option="{ option }">
										<span class="flex min-w-0 flex-col">
											<span class="truncate text-xs font-medium">
												{{ option.name }} {{ option.lastname }}
											</span>
											<span class="truncate text-[11px] text-gray-500">
												{{ option.email }}
											</span>
										</span>
									</template>
								</SearchSelect>
							</FormField>
						</HasPermission>
					</template>
				</CardContent>
			</Card>
		</Collapse>

		<!-- El padding inferior NO es decorativo: el widget de chat vive fijo
		     abajo a la derecha y se monta encima del botón de crear. Dentro de
		     los modales el choque es peor porque el contenido es más angosto y
		     el botón queda justo debajo de las burbujas. Va sin breakpoint
		     porque el modal se ve en escritorio y ahí también tapa. -->
		<div class="flex justify-between gap-3 pb-24">
			<Button
				type="button"
				variant="outline"
				@click.prevent="$emit('cancelForm')"
			>
				Volver
			</Button>

			<HasPermission
				:name-permission="'create-users'"
				:entity-type="'App\\Models\\Company'"
			>
				<Button
					type="button"
					class="bg-[#253C70] text-white hover:bg-[#1c2f59]"
					:disabled="createUserMutationLoading"
					@click.prevent="$emit('sendForm')"
				>
					{{ createUserMutationLoading ? 'Creando…' : 'Crear usuario' }}
				</Button>
			</HasPermission>
		</div>
	</form>
</template>
